import { Maximize, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import type { Shape } from "@/api/client";
import { Button } from "@/ui/Button";
import { bboxToCanvas } from "@/viewer/bbox";
import type { BBoxNorm } from "@/viewer/bbox";
import { ShapeView } from "@/viewer/shapes";
import type { PageSize } from "@/viewer/shapes";

/** Until the raster has loaded, a landscape sheet is the best guess at its shape. */
const GUESS: PageSize = { w: 1600, h: 1000 };
const MIN_SPAN = 0.004;

/** `view` scaled by `factor` (below 1 zooms in) about the normalised point (cx, cy). */
export function zoomView(view: BBoxNorm, factor: number, cx: number, cy: number): BBoxNorm {
  const w = Math.min(1.5, Math.max(MIN_SPAN, (view.x1 - view.x0) * factor));
  const h = Math.min(1.5, Math.max(MIN_SPAN, (view.y1 - view.y0) * factor));
  const fx = (cx - view.x0) / (view.x1 - view.x0 || 1);
  const fy = (cy - view.y0) / (view.y1 - view.y0 || 1);
  const x0 = cx - fx * w;
  const y0 = cy - fy * h;
  return { x0, y0, x1: x0 + w, y1: y0 + h };
}

/**
 * How wide the visible part of the sheet is, in page units. The view box keeps its
 * aspect inside the element ("meet"), so a tall window in a wide element shows more
 * than the view box is wide.
 */
export function visibleWidth(boxW: number, boxH: number, elW: number, elH: number): number {
  if (elW <= 0 || elH <= 0 || boxH <= 0) {
    return boxW;
  }
  return Math.max(boxW, boxH * (elW / elH));
}

/** A box with room around it: `pad` of its own size each side, at least a sliver. */
export function windowAround(b: BBoxNorm, pad = 1.5): BBoxNorm {
  const w = Math.max(Math.abs(b.x1 - b.x0), 0.01);
  const h = Math.max(Math.abs(b.y1 - b.y0), 0.01);
  const x0 = Math.min(b.x0, b.x1);
  const y0 = Math.min(b.y0, b.y1);
  return { x0: x0 - pad * w, y0: y0 - pad * h, x1: x0 + w + pad * w, y1: y0 + h + pad * h };
}

/** What a drawing layer needs to put marks on the sheet. */
export interface ViewerGeometry {
  page: PageSize;
  viewWidth: number;
  /** A pointer position as page-normalised coordinates, or null outside the sheet's SVG. */
  toPage: (clientX: number, clientY: number) => [number, number] | null;
}

interface Props {
  /** The page raster. */
  src: string;
  /** The window shown first, page-normalised. */
  window: BBoxNorm;
  /** The panel being looked at. */
  focus: BBoxNorm;
  /** Other panels of the same type on this sheet. */
  others?: readonly BBoxNorm[];
  colour: string;
  shapes?: readonly Shape[];
  /** False while a drawing tool owns the pointer. */
  pan?: boolean;
  label: string;
  children?: (geometry: ViewerGeometry) => ReactNode;
}

/**
 * One panel on its sheet, full size: the page raster as an SVG image whose view box is
 * the window being looked at, so zoom and pan are a change of view box and everything
 * drawn on top stays in page coordinates.
 */
export function TypeViewer({
  src,
  window: start,
  focus,
  others = [],
  colour,
  shapes = [],
  pan = true,
  label,
  children,
}: Props) {
  const svg = useRef<SVGSVGElement | null>(null);
  const [page, setPage] = useState<PageSize | null>(null);
  const [view, setView] = useState<BBoxNorm>(start);
  const drag = useRef<{ x: number; y: number; view: BBoxNorm } | null>(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = svg.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return;
    }
    const watch = new ResizeObserver(([entry]) => {
      if (entry) {
        setFrame({ w: entry.contentRect.width, h: entry.contentRect.height });
      }
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  useEffect(() => {
    setView(start);
  }, [start]);

  useEffect(() => {
    let live = true;
    setPage(null);
    const img = new Image();
    img.onload = () => {
      if (live && img.naturalWidth > 0) {
        setPage({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.src = src;
    return () => {
      live = false;
    };
  }, [src]);

  const size = page ?? GUESS;

  const toPage = useCallback(
    (clientX: number, clientY: number): [number, number] | null => {
      const el = svg.current;
      const ctm = el?.getScreenCTM?.();
      if (!el || !ctm) {
        return null;
      }
      const point = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
      return [point.x / size.w, point.y / size.h];
    },
    [size.w, size.h],
  );

  const zoomBy = (factor: number, at?: [number, number] | null) => {
    setView((v) => {
      const [cx, cy] = at ?? [(v.x0 + v.x1) / 2, (v.y0 + v.y1) / 2];
      return zoomView(v, factor, cx, cy);
    });
  };

  useEffect(() => {
    const el = svg.current;
    if (!el) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomBy(event.deltaY > 0 ? 1.15 : 1 / 1.15, toPage(event.clientX, event.clientY));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [toPage]);

  const viewBox = `${view.x0 * size.w} ${view.y0 * size.h} ${(view.x1 - view.x0) * size.w} ${
    (view.y1 - view.y0) * size.h
  }`;
  const viewWidth = visibleWidth(
    (view.x1 - view.x0) * size.w,
    (view.y1 - view.y0) * size.h,
    frame.w,
    frame.h,
  );
  const box = (b: BBoxNorm) => bboxToCanvas(b, size.w, size.h);
  const target = box(focus);

  return (
    <div className="relative h-full w-full overflow-hidden bg-stage">
      <svg
        ref={svg}
        role="img"
        aria-label={label}
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full touch-none select-none"
        style={{ cursor: pan ? (drag.current ? "grabbing" : "grab") : "crosshair" }}
        onPointerDown={(event) => {
          if (!pan || event.button !== 0) {
            return;
          }
          drag.current = { x: event.clientX, y: event.clientY, view };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          const from = drag.current;
          const ctm = svg.current?.getScreenCTM?.();
          if (!from || !ctm || !ctm.a) {
            return;
          }
          const dx = (event.clientX - from.x) / ctm.a / size.w;
          const dy = (event.clientY - from.y) / ctm.d / size.h;
          setView({
            x0: from.view.x0 - dx,
            y0: from.view.y0 - dy,
            x1: from.view.x1 - dx,
            y1: from.view.y1 - dy,
          });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
      >
        <image
          href={src}
          x={0}
          y={0}
          width={size.w}
          height={size.h}
          preserveAspectRatio="none"
          opacity={page ? 1 : 0}
        />
        {others.map((b, i) => {
          const r = box(b);
          return (
            <rect
              key={i}
              {...r}
              fill="none"
              stroke={colour}
              strokeOpacity={0.55}
              strokeDasharray="6 4"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        <rect
          {...target}
          fill={colour}
          fillOpacity={0.12}
          stroke={colour}
          strokeWidth={2.5}
          vectorEffect="non-scaling-stroke"
          data-focus
        />
        {shapes.map((shape, i) => (
          <ShapeView key={i} shape={shape} page={size} viewWidth={viewWidth} />
        ))}
        {children?.({ page: size, viewWidth, toPage })}
      </svg>
      <div className="absolute right-3 bottom-3 flex gap-1 rounded-lg border border-line bg-surface/95 p-1 shadow-card">
        <Button
          size="sm"
          variant="ghost"
          className="w-8 px-0"
          aria-label="Zoom in"
          onClick={() => zoomBy(1 / 1.4)}
        >
          <ZoomIn aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="w-8 px-0"
          aria-label="Zoom out"
          onClick={() => zoomBy(1.4)}
        >
          <ZoomOut aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="w-8 px-0"
          aria-label="Fit the panel"
          onClick={() => setView(start)}
        >
          <Maximize aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
